import { HStack, PageContainer, PageHeader, Spread, Text, VStack } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { Note } from '@/components/Note'
import { PendingChanges } from '@/components/PendingChanges'
import usePermissions from '@/hooks/permissions/usePermissions'
import {
  FederalExciseGridRow,
  federalExciseExportQueryOptions,
  federalExciseSearchSchema,
  toFederalExciseGridRow,
  useFederalExciseMappings,
  useUpdateFederalExciseMappings,
} from '@/hooks/queries/crt/useFederalExcise'
import { useLastUpdatedFederalExciseLog } from '@/hooks/queries/useAuditTrails'
import { formatLastUpdatedAssumptionLog } from '@/lib/utils/crt'
import { queryClient } from '@/main'
import { UpdateFederalExciseMappings } from '@repo/backend/crt'
import {
  MarkingCode,
  PRODUCT_CATEGORIES,
  PRODUCT_CATEGORY,
  PRODUCT_CATEGORY_LABEL_KEYS,
  ProductCategory,
} from '@repo/constants'
import { hasPermissions } from '@repo/permissions'
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AlertTriangleIcon, ExternalLinkIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MarginsNcDataTable } from '../../../customer-investments/insights/-shared/margins/MarginsNcDataTable'
import { EditedMarkingValues } from '../../../customer-investments/insights/-shared/margins/types'
import { UploadFederalExciseDataDialog } from './-components/UploadFederalExciseDataDialog'

export const Route = createFileRoute('/_auth/_modules/crt/materials/insights/federal-excise/')({
  component: RouteComponent,
  validateSearch: (search) => federalExciseSearchSchema.parse(search),
})

const DEFAULT_PRODUCT_CATEGORY: ProductCategory = PRODUCT_CATEGORY.FMC

// Edits are keyed by category too, so switching the grid keeps the pending changes of the other grids.
function getRowKey(row: FederalExciseGridRow) {
  return `${row.productCategory}|${row.key}`
}

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common', 'materials'])
  const { permissions } = usePermissions()
  const canEdit = hasPermissions(permissions, 'crt_federal_excise_edit')
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const productCategory = search.productCategory ?? DEFAULT_PRODUCT_CATEGORY
  const isFmc = productCategory === PRODUCT_CATEGORY.FMC

  const [editedValues, setEditedValues] = useState<Map<string, EditedMarkingValues>>(new Map())

  const { data, isPending, error } = useFederalExciseMappings(productCategory)
  const { data: lastUpdatedTrail } = useLastUpdatedFederalExciseLog()
  const lastUpdatedLog = formatLastUpdatedAssumptionLog(lastUpdatedTrail)

  const { mutate: updateMappings, isPending: isSaving } = useUpdateFederalExciseMappings()

  const uncoveredMaterialsCount = data?.uncoveredMaterialsCount ?? 0
  const rows: FederalExciseGridRow[] = (data?.results ?? []).map(toFederalExciseGridRow)

  const handleProductCategoryChange = (next: string) => {
    navigate({ search: { productCategory: next as ProductCategory } })
  }

  const handleCommitValue = (row: FederalExciseGridRow, marking: MarkingCode, value: string) => {
    const originalValue = row[marking] ?? ''
    const key = getRowKey(row)

    setEditedValues((prev) => {
      const next = new Map(prev)
      const existing = next.get(key) ?? {}
      const isUnchanged =
        value === originalValue ||
        (value !== '' && originalValue !== '' && Number(value) === Number(originalValue))

      if (isUnchanged) {
        const rest: EditedMarkingValues = { ...existing }
        delete rest[marking]
        if (Object.keys(rest).length === 0) {
          next.delete(key)
        } else {
          next.set(key, rest)
        }
        return next
      }

      next.set(key, { ...existing, [marking]: value })
      return next
    })
  }

  const handleSaveChanges = () => {
    if (editedValues.size === 0) return

    const updates: UpdateFederalExciseMappings['updates'] = []
    for (const [rowKey, values] of editedValues) {
      const [category, key] = rowKey.split('|')
      if (!category || !key) continue
      for (const [marking, value] of Object.entries(values) as [MarkingCode, string][]) {
        updates.push({
          productCategory: category as ProductCategory,
          key,
          markingCode: marking,
          priceFedExcise: value === '' ? null : value,
        })
      }
    }
    if (updates.length === 0) return

    updateMappings({ updates }, { onSuccess: () => setEditedValues(new Map()) })
  }

  const handleBulkEdit = (
    selectedRows: FederalExciseGridRow[],
    values: Partial<Record<MarkingCode, string>>,
  ) => {
    if (selectedRows.length === 0 || Object.keys(values).length === 0) return

    const updates: UpdateFederalExciseMappings['updates'] = selectedRows.flatMap((row) =>
      (Object.entries(values) as [MarkingCode, string][]).map(([marking, value]) => ({
        productCategory: row.productCategory,
        key: row.key,
        markingCode: marking,
        priceFedExcise: value,
      })),
    )

    updateMappings(
      { updates },
      {
        onSuccess: () => {
          setEditedValues((prev) => {
            if (prev.size === 0) return prev
            const next = new Map(prev)
            for (const row of selectedRows) {
              next.delete(getRowKey(row))
            }
            return next.size === prev.size ? prev : next
          })
        },
      },
    )
  }

  const areChangesPending = editedValues.size > 0

  return (
    <PageContainer maxWidth="wide" className="pb-28">
      <Block shouldBlock={areChangesPending} />

      <Spread align="start">
        <VStack gap="xs">
          <PageHeader>{t('federalExcise.pageHeader')}</PageHeader>
          <Text tone="muted">{t('federalExcise.description')}</Text>
        </VStack>
        <HStack gap="sm">
          <PermissionsGuard required={['crt_federal_excise_edit']}>
            <UploadFederalExciseDataDialog />
          </PermissionsGuard>
          <PermissionsGuard required={['crt_federal_excise_view']}>
            {/* The export covers every category, not only the grid shown, so it is never empty. */}
            <ExportCsvButton
              getCsvExport={() => queryClient.ensureQueryData(federalExciseExportQueryOptions())}
              rowCount={1}
              canExceedRowCountLimit
            />
          </PermissionsGuard>
        </HStack>
      </Spread>

      {uncoveredMaterialsCount > 0 && (
        <Note
          tone="warning"
          icon={AlertTriangleIcon}
          note={t('federalExcise.uncoveredMaterials.banner', { count: uncoveredMaterialsCount })}
          action={
            <PermissionsGuard required={['crt_product_types_edit']}>
              <Button type="button" variant="secondary" size="sm" asChild>
                <Link to="/crt/materials/insights/product-types">
                  <ExternalLinkIcon className="size-4" />
                  {t('federalExcise.uncoveredMaterials.editProductTypes')}
                </Link>
              </Button>
            </PermissionsGuard>
          }
        />
      )}

      <VStack gap="md">
        <Select value={productCategory} onValueChange={handleProductCategoryChange}>
          <SelectTrigger id="federal-excise-product-category-select" className="w-72 bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRODUCT_CATEGORIES.map((category) => (
              <SelectItem key={category} value={category}>
                {t(PRODUCT_CATEGORY_LABEL_KEYS[category], { ns: 'materials' })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <MarginsNcDataTable<FederalExciseGridRow>
          rows={rows}
          isPending={isPending}
          error={error}
          editedValues={editedValues}
          canEdit={canEdit}
          domIdPrefix={`federal-excise-${productCategory.toLowerCase()}`}
          valueKind="currency"
          i18nKeyPrefix="federalExcise"
          rowLabel={t(
            isFmc ? 'federalExcise.table.materialGroup' : 'federalExcise.table.productType',
          )}
          getRowKey={getRowKey}
          onCommitValue={handleCommitValue}
          onBulkEdit={handleBulkEdit}
        />
      </VStack>

      <PermissionsGuard required={['crt_federal_excise_edit']}>
        <PendingChanges
          areChangesPending={areChangesPending}
          lastUpdatedLog={lastUpdatedLog}
          isSavePending={isSaving}
          onSaveChanges={handleSaveChanges}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
